import useCSSVariables from '@/utils/hooks/useCSSVariables';
import { StyleSheet } from '@react-pdf/renderer';
import { useMemo } from 'react';

/**
 * Every colour below is derived from the brand primary color.
 * The document reads as one brand family with consistent theming.
 */

export const layout = {
    page: 36,
    columns: {
        asset: '27%',
        total: '15%',
        rate: '17%',
        value: '21%',
        allocation: '20%'
    },
    /** Breathing room between the live value figures and the allocation column. */
    allocationGutter: 22
};

const usePDFTheme = () => {
    const cssVariables = useCSSVariables();
    const fontFamily = cssVariables['--font-sans'];

    const palette = useMemo(() => {
        return {
            primary: cssVariables['--color-brand-primary-rgb-500'],
            primaryDark: cssVariables['--color-brand-primary-rgb-600'],
            primaryDeep: cssVariables['--color-brand-primary-rgb-700'],
            primaryInk: cssVariables['--color-brand-primary-rgb-800'],
            primaryMid: cssVariables['--color-brand-primary-rgb-400'],
            primarySoft: cssVariables['--color-brand-primary-rgb-300'],
            primaryTint: cssVariables['--color-brand-primary-rgb-200'],
            primaryWash: cssVariables['--color-brand-primary-rgb-100'],
            canvas: cssVariables['--color-brand-primary-rgb-50'],
            surface: '#FFFFFF',
            border: cssVariables['--color-brand-primary-rgb-200'],
            borderStrong: cssVariables['--color-brand-primary-rgb-300'],
            ink: cssVariables['--color-brand-primary-rgb-900'],
            inkMuted: cssVariables['--color-brand-primary-rgb-600'],
            inkFaint: cssVariables['--color-brand-primary-rgb-400'],
            neutral: cssVariables['--color-brand-neutral-500'],
            onPrimary: '#FFFFFF'
        };
    }, [cssVariables]);

    const base = useMemo(() => {
        return StyleSheet.create({
            page: {
                backgroundColor: palette.canvas,
                paddingTop: layout.page + 10,
                paddingBottom: layout.page + 14,
                paddingHorizontal: layout.page,
                fontFamily,
                color: palette.ink
            },
            footer: {
                position: 'absolute',
                bottom: 22,
                left: layout.page,
                right: layout.page,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTopWidth: 1,
                borderTopColor: palette.border,
                paddingTop: 8
            },
            footerText: {
                fontSize: 7,
                letterSpacing: 0.6,
                color: palette.inkMuted,
                fontFamily,
                fontWeight: 400
            },
            eyebrow: {
                fontSize: 7.5,
                letterSpacing: 1.6,
                color: palette.primaryDark,
                fontFamily,
                fontWeight: 600
            },
            sectionTitle: {
                fontSize: 16,
                marginTop: 4,
                color: palette.ink,
                fontFamily,
                fontWeight: 600
            }
        });
    }, [palette, fontFamily]);

    return {
        fontFamily,
        palette,
        layout,
        base
    };
};

export default usePDFTheme;
